import {createSlice, createAsyncThunk, PayloadAction} from '@reduxjs/toolkit';
import api from '../../services/api';
import {toast} from 'react-toastify';
import {CartState, Cart, AddToCartRequest, Product} from '../../types';
import {AxiosError} from 'axios';
import {logout} from './authSlice';

export const fetchCart = createAsyncThunk<Cart, void>(
    'cart/fetchCart',
    async (_, {rejectWithValue}) => {
        try {
            const response = await api.get('/cart');
            return response.data;
        } catch (error) {
            const axiosError = error as AxiosError<{ message: string }>;
            return rejectWithValue(axiosError.response?.data?.message || 'Failed to fetch cart');
        }
    }
);

export const addToCart = createAsyncThunk<Cart, AddToCartRequest>(
    'cart/addToCart',
    async ({productId, quantity = 1}, {rejectWithValue}) => {
        try {
            const response = await api.post('/cart/items', {product_id: productId, quantity});
            return response.data;
        } catch (error) {
            const axiosError = error as AxiosError<{ message: string }>;
            return rejectWithValue(axiosError.response?.data?.message || 'Failed to add to cart');
        }
    }
);

export const updateCartItem = createAsyncThunk<Cart, { productId: number; quantity: number }>(
    'cart/updateCartItem',
    async ({productId, quantity}, {rejectWithValue}) => {
        try {
            const response = await api.put(`/cart/items/${productId}`, {quantity});
            return response.data;
        } catch (error) {
            const axiosError = error as AxiosError<{ message: string }>;
            return rejectWithValue(axiosError.response?.data?.message || 'Failed to update cart');
        }
    }
);

export const removeFromCart = createAsyncThunk<number, number>(
    'cart/removeFromCart',
    async (productId, {rejectWithValue}) => {
        try {
            await api.delete(`/cart/items/${productId}`);
            return productId;
        } catch (error) {
            const axiosError = error as AxiosError<{ message: string }>;
            return rejectWithValue(axiosError.response?.data?.message || 'Failed to remove from cart');
        }
    }
);

export const clearCart = createAsyncThunk<null, void>(
    'cart/clearCart',
    async (_, {rejectWithValue}) => {
        try {
            await api.delete('/cart');
            return null;
        } catch (error) {
            const axiosError = error as AxiosError<{ message: string }>;
            return rejectWithValue(axiosError.response?.data?.message || 'Failed to clear cart');
        }
    }
);

export const mergeGuestCartToAuth = createAsyncThunk<Cart, void>(
    'cart/mergeGuestCartToAuth',
    async (_, {rejectWithValue, getState}) => {
        try {
            // Get guest cart items from sessionStorage
            const savedCart = sessionStorage.getItem('guestCart');
            if (!savedCart) {
                // No guest cart to merge, just fetch user cart
                const response = await api.get('/cart');
                return response.data;
            }

            const guestCart = JSON.parse(savedCart);
            if (!guestCart.items || guestCart.items.length === 0) {
                // Empty guest cart, just fetch user cart
                const response = await api.get('/cart');
                return response.data;
            }

            // Add each guest cart item to user cart
            for (const item of guestCart.items) {
                try {
                    await api.post('/cart/items', {
                        product_id: item.product.id,
                        quantity: item.quantity
                    });
                } catch (error) {
                    console.error('Failed to add item to cart:', error);
                }
            }

            // Clear guest cart from sessionStorage
            sessionStorage.removeItem('guestCart');

            // Fetch updated user cart
            const response = await api.get('/cart');
            return response.data;
        } catch (error) {
            const axiosError = error as AxiosError<{ message: string }>;
            return rejectWithValue(axiosError.response?.data?.message || 'Failed to merge cart');
        }
    }
);

// Load guest cart from sessionStorage if user is not authenticated
const loadGuestCartFromStorage = (): Partial<CartState> => {
    try {
        const savedCart = sessionStorage.getItem('guestCart');
        if (savedCart) {
            const cart = JSON.parse(savedCart);
            return {
                items: cart.items || [],
                totalAmount: cart.totalAmount || 0,
                totalItems: cart.totalItems || 0,
            };
        }
    } catch (error) {
        console.error('Error loading guest cart from storage:', error);
    }
    return {};
};

const guestCartData = loadGuestCartFromStorage();

const initialState: CartState = {
    items: guestCartData.items || [],
    totalAmount: guestCartData.totalAmount || 0,
    totalItems: guestCartData.totalItems || 0,
    loading: false,
    error: null,
};


const cartSlice = createSlice({
    name: 'cart',
    initialState,
    reducers: {
        clearError: (state) => {
            state.error = null;
        },
        // Local cart management for non-authenticated users
        addToLocalCart: (state, action: PayloadAction<{ product: Product; quantity: number }>) => {
            const {product, quantity} = action.payload;
            const existingItem = state.items.find(item => item.product.id === product.id);

            if (existingItem) {
                existingItem.quantity += quantity;
            } else {
                state.items.push({
                    id: Date.now(),
                    product,
                    quantity,
                    price: product.price ?? 0,
                });
            }

            state.totalItems = state.items.length;
            state.totalAmount = state.items.reduce(
                (total, item) => total + ((item.product.price ?? 0) * item.quantity),
                0
            );

            // Save to sessionStorage
            sessionStorage.setItem('guestCart', JSON.stringify({
                items: state.items,
                totalAmount: state.totalAmount,
                totalItems: state.totalItems
            }));
        },
        removeFromLocalCart: (state, action: PayloadAction<number>) => {
            state.items = state.items.filter(item => item.id !== action.payload);
            state.totalItems = state.items.length;
            state.totalAmount = state.items.reduce(
                (total, item) => total + ((item.product.price ?? 0) * item.quantity),
                0
            );

            // Save to sessionStorage
            sessionStorage.setItem('guestCart', JSON.stringify({
                items: state.items,
                totalAmount: state.totalAmount,
                totalItems: state.totalItems
            }));
        },
        updateLocalCartItem: (state, action: PayloadAction<{ itemId: number; quantity: number }>) => {
            const {itemId, quantity} = action.payload;
            const item = state.items.find(item => item.id === itemId);
            if (item) {
                item.quantity = quantity;
                state.totalItems = state.items.length;
                state.totalAmount = state.items.reduce(
                    (total, item) => total + ((item.product.price ?? 0) * item.quantity),
                    0
                );

                // Save to sessionStorage
                sessionStorage.setItem('guestCart', JSON.stringify({
                    items: state.items,
                    totalAmount: state.totalAmount,
                    totalItems: state.totalItems
                }));
            }
        },
        clearLocalCart: (state) => {
            state.items = [];
            state.totalAmount = 0;
            state.totalItems = 0;

            // Clear sessionStorage
            sessionStorage.removeItem('guestCart');
        },
        // Switch from authenticated cart to guest cart
        switchToGuestCart: (state) => {
            // Clear current auth cart state
            state.items = [];
            state.totalAmount = 0;
            state.totalItems = 0;
            state.error = null;
            
            // Load guest cart from sessionStorage if it exists
            const guestCartData = loadGuestCartFromStorage();
            if (guestCartData.items && guestCartData.items.length > 0) {
                state.items = guestCartData.items;
                state.totalAmount = guestCartData.totalAmount || 0;
                state.totalItems = guestCartData.totalItems || 0;
            }
        },
    },
    extraReducers: (builder) => {
        // Fetch Cart
        builder
            .addCase(fetchCart.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchCart.fulfilled, (state, action) => {
                state.loading = false;
                const payload = action.payload as any;
                state.items = payload.items || [];
                state.totalAmount = payload.total || payload.totalAmount || 0;
                state.totalItems = payload.total_items || payload.totalItems || 0;
            })
            .addCase(fetchCart.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            });

        // Add to Cart
        builder
            .addCase(addToCart.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(addToCart.fulfilled, (state, action) => {
                state.loading = false;
                const payload = action.payload as any;
                state.items = payload.items || [];
                state.totalAmount = payload.total || payload.totalAmount || 0;
                state.totalItems = payload.total_items || payload.totalItems || 0;
            })
            .addCase(addToCart.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
                toast.error(action.payload as string);
            });

        // Update Cart Item
        builder
            .addCase(updateCartItem.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(updateCartItem.fulfilled, (state, action) => {
                state.loading = false;
                const payload = action.payload as any;
                state.items = payload.items || [];
                state.totalAmount = payload.total || payload.totalAmount || 0;
                state.totalItems = payload.total_items || payload.totalItems || 0;
            })
            .addCase(updateCartItem.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
                toast.error(action.payload as string);
            });

        // Remove from Cart
        builder
            .addCase(removeFromCart.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(removeFromCart.fulfilled, (state, action) => {
                state.loading = false;
                state.items = state.items.filter(item => item.product.id !== action.payload);
                state.totalItems = state.items.length;
                state.totalAmount = state.items.reduce(
                    (total, item) => total + ((item.product.price ?? 0) * item.quantity),
                    0
                );
                toast.success('Item removed from cart');
            })
            .addCase(removeFromCart.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
                toast.error(action.payload as string);
            });

        // Clear Cart
        builder
            .addCase(clearCart.fulfilled, (state) => {
                state.items = [];
                state.totalAmount = 0;
                state.totalItems = 0;
                toast.success('Cart cleared');
            });

        // Merge Guest Cart to Auth
        builder
            .addCase(mergeGuestCartToAuth.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(mergeGuestCartToAuth.fulfilled, (state, action) => {
                state.loading = false;
                const payload = action.payload as any;
                state.items = payload.items || [];
                state.totalAmount = payload.total || payload.totalAmount || 0;
                state.totalItems = payload.total_items || payload.totalItems || 0;
            })
            .addCase(mergeGuestCartToAuth.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
                console.error('Failed to merge guest cart:', action.payload);
            });

        // Handle logout - switch to guest cart
        builder
            .addCase(logout.fulfilled, (state) => {
                // Clear current auth cart state
                state.items = [];
                state.totalAmount = 0;
                state.totalItems = 0;
                state.error = null;
                
                // Load guest cart from sessionStorage if it exists
                const guestCartData = loadGuestCartFromStorage();
                if (guestCartData.items && guestCartData.items.length > 0) {
                    state.items = guestCartData.items;
                    state.totalAmount = guestCartData.totalAmount || 0;
                    state.totalItems = guestCartData.totalItems || 0;
                }
            });
    },
});

export const {
    clearError,
    addToLocalCart,
    removeFromLocalCart,
    updateLocalCartItem,
    clearLocalCart,
    switchToGuestCart,
} = cartSlice.actions;

export default cartSlice.reducer;